import { useEffect, useState, type CSSProperties } from "react";
import Button from "@mui/material/Button";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { QuoteCardView } from "../Components/QuoteCardView";
import { QuoteListView } from "../Components/QuoteListView";
import { QuoteForm } from "../Features/Quotes/QuoteForm";
import { type Quote } from "../types/quote";

type QuoteView = "cards" | "list";

export function Quotes() {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<QuoteView>("cards");
  const [addQuoteOpen, setAddQuoteOpen] = useState(false);

  useEffect(() => {
    loadQuotes();
  }, []);

  useEffect(() => {
    if (!addQuoteOpen) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setAddQuoteOpen(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [addQuoteOpen]);

  function loadQuotes() {
    setLoading(true);

    fetch("/api/Quotes")
      .then((response) => response.json())
      .then((data: Quote[]) => {
        setQuotes(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching quotes:", error);
        setLoading(false);
      });
  }

  if (loading) {
    return <div style={styles.status}>Loading quotes...</div>;
  }

  return (
    <main style={styles.page}>
      <header style={styles.header}>
        <h1 style={styles.heading}>Daily Quotes</h1>
        <div style={styles.headerActions}>
          <Button variant="contained" onClick={() => setAddQuoteOpen(true)}>
            Add quote
          </Button>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={view}
            onChange={(_, nextView: QuoteView | null) => {
              if (nextView) {
                setView(nextView);
              }
            }}
            aria-label="Quote view"
          >
            <ToggleButton value="cards" aria-label="Card view">
              Cards
            </ToggleButton>
            <ToggleButton value="list" aria-label="List view">
              List
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      </header>

      {addQuoteOpen && (
        <div
          style={styles.modalOverlay}
          role="presentation"
          onClick={() => setAddQuoteOpen(false)}
        >
          <section
            style={styles.formPanel}
            role="dialog"
            aria-modal="true"
            aria-label="Add quote"
            onClick={(event) => event.stopPropagation()}
          >
            <header style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Add quote</h2>
              <Button type="button" onClick={() => setAddQuoteOpen(false)}>
                Close
              </Button>
            </header>
            <QuoteForm
              onSubmitted={() => {
                setAddQuoteOpen(false);
                loadQuotes();
              }}
            />
          </section>
        </div>
      )}

      {view === "cards" ? (
        <QuoteCardView quotes={quotes} />
      ) : (
        <QuoteListView quotes={quotes} />
      )}
    </main>
  );
}

const styles = {
  page: {
    width: "min(920px, calc(100vw - 32px))",
    margin: "0 auto",
    padding: "32px 0",
    textAlign: "left",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "24px",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  heading: {
    margin: 0,
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 40,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    background: "rgba(0, 0, 0, 0.42)",
    padding: "72px 16px 16px",
    overflowY: "auto",
  },
  formPanel: {
    width: "min(820px, 100%)",
    border: "1px solid #2e303a",
    borderRadius: "8px",
    background: "#16171d",
    color: "white",
    boxShadow: "var(--shadow)",
    padding: "16px",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "16px",
  },
  modalTitle: {
    margin: 0,
    color: "white",
    fontSize: "22px",
    fontWeight: 500,
  },
  status: {
    padding: "32px",
  },
} satisfies Record<string, CSSProperties>;
